import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  BarChart3,
  Globe,
  Monitor,
  Smartphone,
  TrendingUp,
  RefreshCw
} from "lucide-react";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ["#0EA5E9", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6"];

const AnalyticsPage = () => {
  const { getAuthHeader } = useAuth();
  const [overview, setOverview] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [countries, setCountries] = useState([]);
  const [browsers, setBrowsers] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeader();
      const [overviewRes, timelineRes, countriesRes, browsersRes, devicesRes] = await Promise.all([
        axios.get(`${API}/analytics/overview`, { headers }),
        axios.get(`${API}/analytics/timeline?days=14`, { headers }),
        axios.get(`${API}/analytics/countries`, { headers }),
        axios.get(`${API}/analytics/browsers`, { headers }),
        axios.get(`${API}/analytics/devices`, { headers })
      ]);
      
      setOverview(overviewRes.data);
      setTimeline(timelineRes.data);
      setCountries(countriesRes.data);
      setBrowsers(browsersRes.data);
      setDevices(devicesRes.data);
    } catch (error) {
      console.error("Failed to fetch analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="analytics-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-mono font-bold tracking-tight flex items-center gap-3">
            <BarChart3 className="w-7 h-7 text-primary" />
            Analytics
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Visitor insights and traffic analysis
          </p>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={fetchData}
          className="gap-2"
          data-testid="refresh-analytics-btn"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-card/50 border-border">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Visits</p>
            <p className="text-2xl font-mono font-bold mt-1">{overview?.total_logs || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-card/50 border-border">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Unique Visitors</p>
            <p className="text-2xl font-mono font-bold mt-1">{overview?.unique_ips || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-card/50 border-border">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Today</p>
            <p className="text-2xl font-mono font-bold mt-1 text-primary">{overview?.today_visitors || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-card/50 border-border">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Threat Rate</p>
            <p className="text-2xl font-mono font-bold mt-1 text-amber-500">
              {overview?.total_logs > 0 
                ? ((overview.threats + overview.suspicious) / overview.total_logs * 100).toFixed(1) 
                : 0}%
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Timeline Chart */}
      <Card className="bg-card/50 border-border" data-testid="timeline-chart">
        <CardHeader>
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-primary" />
            Visitor Timeline (14 Days)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] w-full">
            {timeline.length > 0 ? (
              <ResponsiveContainer width="99%" height={300}>
                <AreaChart data={timeline}>
                  <defs>
                    <linearGradient id="colorVisits" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0EA5E9" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#0EA5E9" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <XAxis 
                    dataKey="date" 
                    tick={{ fill: '#71717A', fontSize: 11 }}
                    tickFormatter={(value) => value.slice(5)}
                    axisLine={{ stroke: '#27272A' }}
                  />
                  <YAxis 
                    tick={{ fill: '#71717A', fontSize: 11 }}
                    axisLine={{ stroke: '#27272A' }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#18181B', 
                      border: '1px solid #27272A',
                      borderRadius: '6px',
                      fontSize: '12px'
                    }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="count" 
                    stroke="#0EA5E9" 
                    fill="url(#colorVisits)"
                    strokeWidth={2}
                    name="Visits"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-muted-foreground">
                No data available
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Charts Row */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Countries */}
        <Card className="bg-card/50 border-border" data-testid="countries-chart">
          <CardHeader>
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Globe className="w-4 h-4 text-primary" />
              Top Countries
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[250px] w-full">
              {countries.length > 0 ? (
                <ResponsiveContainer width="99%" height={250}>
                  <BarChart data={countries.slice(0, 5)} layout="vertical">
                    <XAxis type="number" tick={{ fill: '#71717A', fontSize: 11 }} />
                    <YAxis 
                      dataKey="country" 
                      type="category" 
                      tick={{ fill: '#71717A', fontSize: 11 }}
                      width={80}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#18181B', 
                        border: '1px solid #27272A',
                        borderRadius: '6px',
                        fontSize: '12px'
                      }}
                    />
                    <Bar dataKey="count" fill="#0EA5E9" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
                  No data available
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Browsers */}
        <Card className="bg-card/50 border-border" data-testid="browsers-chart">
          <CardHeader>
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Monitor className="w-4 h-4 text-primary" />
              Browsers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[250px] w-full">
              {browsers.length > 0 ? (
                <ResponsiveContainer width="99%" height={250}>
                  <PieChart>
                    <Pie
                      data={browsers}
                      dataKey="count"
                      nameKey="browser"
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                    >
                      {browsers.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#18181B', 
                        border: '1px solid #27272A',
                        borderRadius: '6px',
                        fontSize: '12px'
                      }}
                    />
                    <Legend 
                      formatter={(value) => <span className="text-xs text-zinc-400">{value}</span>}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
                  No data available
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Devices */}
        <Card className="bg-card/50 border-border" data-testid="devices-chart">
          <CardHeader>
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Smartphone className="w-4 h-4 text-primary" />
              Devices
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[250px] w-full">
              {devices.length > 0 ? (
                <ResponsiveContainer width="99%" height={250}>
                  <PieChart>
                    <Pie
                      data={devices}
                      dataKey="count"
                      nameKey="device"
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                    >
                      {devices.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#18181B', 
                        border: '1px solid #27272A',
                        borderRadius: '6px',
                        fontSize: '12px'
                      }}
                    />
                    <Legend 
                      formatter={(value) => <span className="text-xs text-zinc-400">{value}</span>}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
                  No data available
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AnalyticsPage;
